/**
 * Calculate the original price for an order item based on product pricing layers
 * This considers base price, variant pricing, variant options pricing, and location pricing
 */
export const calculateOriginalPriceForOrderItem = (
  product: any,
  selectedVariantOptions?: Array<{ optionName: string; optionValue: string }>,
  locationId?: string,
): number => {
  // Start with base product price
  let originalPrice = product.price || 0;

  // 1. Check variant combinations first (highest priority)
  if (
    selectedVariantOptions &&
    selectedVariantOptions.length > 0 &&
    product.variantCombinations
  ) {
    const variantCombination = product.variantCombinations.find(
      (combo: any) => {
        return selectedVariantOptions.every((opt) => {
          const comboOption = combo.options?.find(
            (c: any) => c.optionName === opt.optionName,
          );
          return comboOption && comboOption.optionValue === opt.optionValue;
        });
      },
    );

    if (variantCombination && variantCombination.price !== undefined) {
      originalPrice = variantCombination.price;
    }
  }

  // 2. Check variant options pricing if no variant combination found
  if (
    originalPrice === 0 &&
    selectedVariantOptions &&
    selectedVariantOptions.length > 0 &&
    product.variantOptions
  ) {
    let variantPrice = product.price || 0;

    selectedVariantOptions.forEach((selectedOpt) => {
      const variantOption = product.variantOptions.find(
        (vo: any) => vo.name === selectedOpt.optionName,
      );

      if (variantOption && variantOption.values) {
        const variantValue = variantOption.values.find(
          (vv: any) => vv.name === selectedOpt.optionValue,
        );

        if (variantValue && variantValue.price !== undefined) {
          // Add the variant price to base price (if it's an add-on)
          // or use it as the main price (if it replaces the base price)
          if (variantValue.addOnPrice !== undefined) {
            variantPrice += variantValue.addOnPrice;
          } else {
            variantPrice = variantValue.price;
          }
        }
      }
    });

    originalPrice = variantPrice;
  }

  // 3. Check location-based pricing if locationId is provided
  if (locationId && product.locationPricing) {
    const locationPrice = product.locationPricing.find(
      (lp: any) => String(lp.locationId) === String(locationId),
    );

    if (locationPrice && locationPrice.price !== undefined) {
      originalPrice = locationPrice.price;
    }
  }

  // 4. Fallback to base product price if still 0
  if (originalPrice === 0 && product.price) {
    originalPrice = product.price;
  }

  return originalPrice;
};

/**
 * Calculate discount information for an order item
 * Returns the discount amount and type if a discount is applied
 */
export const calculateOrderItemDiscount = (
  product: any,
  itemPrice: number,
  selectedVariantOptions?: Array<{ optionName: string; optionValue: string }>,
  locationId?: string,
): {
  discountPerUnit: number;
  discountType: "amount" | "percentage" | undefined;
  discountValue: number | undefined;
} => {
  const originalPrice = calculateOriginalPriceForOrderItem(
    product,
    selectedVariantOptions,
    locationId,
  );

  if (originalPrice > itemPrice) {
    const discountAmount = originalPrice - itemPrice;
    const discountPercentage = (discountAmount / originalPrice) * 100;

    // Determine if it's likely a percentage discount (round numbers)
    const isPercentageDiscount =
      Number.isInteger(discountPercentage) && discountPercentage <= 50;

    return {
      discountPerUnit: discountAmount,
      discountType: isPercentageDiscount ? "percentage" : "amount",
      discountValue: isPercentageDiscount ? discountPercentage : discountAmount,
    };
  }

  return {
    discountPerUnit: 0,
    discountType: undefined,
    discountValue: undefined,
  };
};
